"use client";

import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import Link from "next/link";
import type { ReactNode } from "react";
import { cn } from "./cn";

export interface ActionMenuItem {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  /** Navigation item — rendered as a real link (middle-click / open in new tab work). */
  href?: string;
  onSelect?: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Tooltip explaining why the item is disabled (e.g. missing permission). */
  title?: string;
}

export interface ActionMenuProps {
  trigger: ReactNode;
  items: (ActionMenuItem | "separator")[];
  ariaLabel: string;
  align?: "start" | "end";
}

/** Row-level actions beyond the one or two shown inline. Real actions only — permission-gated. */
export function ActionMenu({ trigger, items, ariaLabel, align = "end" }: ActionMenuProps) {
  // Drop leading/trailing/duplicated separators left behind by permission-gated items.
  const visible = items.filter(
    (item, index, all) =>
      item !== "separator" ||
      (index > 0 && index < all.length - 1 && all[index - 1] !== "separator"),
  );
  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="ui-menu-content"
          align={align}
          sideOffset={6}
          aria-label={ariaLabel}
        >
          {visible.map((item, index) =>
            item === "separator" ? (
              <DropdownMenu.Separator key={`sep-${index}`} className="ui-menu-separator" />
            ) : item.href && !item.disabled ? (
              <DropdownMenu.Item
                key={item.key}
                asChild
                className={cn("ui-menu-item", item.danger && "danger")}
              >
                <Link href={item.href} title={item.title}>
                  <span className="ui-menu-item-icon" aria-hidden="true">
                    {item.icon}
                  </span>
                  {item.label}
                </Link>
              </DropdownMenu.Item>
            ) : (
              <DropdownMenu.Item
                key={item.key}
                className={cn("ui-menu-item", item.danger && "danger")}
                disabled={item.disabled}
                title={item.title}
                onSelect={item.onSelect}
              >
                <span className="ui-menu-item-icon" aria-hidden="true">
                  {item.icon}
                </span>
                {item.label}
              </DropdownMenu.Item>
            ),
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export interface RowActionsProps {
  /** Entity name — the trigger is announced as "Ações de <name>". */
  name: string;
  items: (ActionMenuItem | "separator")[];
  disabled?: boolean;
}

/**
 * Standard trailing "⋯" menu of a table row (padrão ASV). The entity name in
 * the first column is the primary way to open the record; everything else
 * (editar, bloquear, revogar, suspender…) lives here. Destructive items go
 * last, after a separator, with `danger`.
 */
export function RowActions({ name, items, disabled }: RowActionsProps) {
  const label = `Ações de ${name}`;
  if (items.every((item) => item === "separator")) return null;
  return (
    <ActionMenu
      ariaLabel={label}
      items={items}
      trigger={
        <button
          type="button"
          className="ui-icon-button ui-row-actions-trigger"
          aria-label={label}
          title="Ações"
          disabled={disabled}
        >
          <MoreHorizontal size={18} aria-hidden="true" />
        </button>
      }
    />
  );
}
